import { createSlice } from "@reduxjs/toolkit";
import { removeSecureStoreItems } from "@utils/index";
import { useSelector } from "react-redux";
import { ProductCategory } from "@utils/constants";
import { Platform } from "react-native";

export const appStateSlice = createSlice({
  name: "appState",
  initialState: {
    isCustomApp: false,
    logo: process.env.EXPO_PUBLIC_LOGIN_LOGO,
    activeProduct: ProductCategory.DEFAULT,
    builderProduct: ProductCategory.DEFAULT,
    requestScreenColor: ProductCategory.DIAMOND,
    onPreBoardingComplete: false,
    postOnboardingCheck: true,
    isAppInitialLoad: true,
    selectedProduct: "",
    navigationHeight: 0,
    orgTheme: null,
    orgTypography: null,
    isCompanyThemeLoaded: false,
    notificationSelected: false,
    orgNavigationCustomisation: null,
    orgTranslationKeys: null,
    orgGlobalCustomization: null,
    orgPDPCustomisation: null,
    orgSearchFilterCustomisation: null,
    orgSearchResultCustomisation: null,
    orgUnifiedProductPagesCustomisation: null,
    listViewCustomisation: null,
    isSearchFilterCustomisationLoad: false,
    isSearchResultCustomisationLoad: false,
    isUnifiedProductPagesLoad: false,
    isNavigationCustomisationLoad: false,
    isGlobalCustomisationLoad: false,
    isPDPCustomisationLoad: false,
    searchResultCount: 0,
    installationIdentifier: null,
    isLoadingConfigurations: false,
    overlayActiveScreens: {
      ExploreScreen: Platform.OS !== "web",
      BottomTabBar: Platform.OS !== "web",
      MoreScreen: Platform.OS !== "web",
      Favorites: Platform.OS !== "web",
      SearchResult: Platform.OS !== "web",
      Request: Platform.OS !== "web",
      AISearchWidget: Platform.OS !== "web",
    },
    overlayActiveScreensWeb: {
      Favorites: true,
      MoreScreen: true,
      SearchResult: true,
      Header: true,
      BulkActions: true,
      AISearchWidget: true,
    },
    adHeight: 0,
    adWidth: 0,
    filterVisible: "",
    loaderSettings: true,
    coachOverlayToast: true,
    loginModalType: "none",
    isSplitFilterVisible: null,
    isFavCollectionVisible: true,
    appVersion: process.env.APP_VERSION,
    lastLoginDetails: [],
    isSignoutUrlRedirecting: false,
    latestExportData: null,
    disableJewelrySettingStoneLinkage: false,
  },
  reducers: {
    setAppState: (state, action) => {
      state = { ...state, ...action.payload };
      return state;
    },
    setOnPreboardingStatus: (state, action) => {
      state.onPreBoardingComplete = action.payload;
    },
    setPostOnboardingCheck: (state, action) => {
      state.postOnboardingCheck = action.payload;
    },
    resetPostOnboardingCheck: (state) => {
      state.postOnboardingCheck = true;
    },
    setAppInitialLoad: (state) => {
      state.isAppInitialLoad = false;
      removeSecureStoreItems(["username", "password"]);
    },
    setProductType: (state, action) => {
      state.activeProduct = action.payload;
    },
    setBuilderProduct: (state, action) => {
      state.builderProduct = action.payload;
    },
    setRequestScreenColor: (state, action) => {
      state.requestScreenColor = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    setNavigationHeight: (state, action) => {
      state.navigationHeight = action.payload;
    },
    setOrgTheme: (state, action) => {
      state.orgTheme = action.payload;
    },
    setOrgTypography: (state, action) => {
      state.orgTypography = action.payload;
    },
    setIsCompanyTheme: (state, action) => {
      state.isCompanyThemeLoaded = action.payload;
    },
    setNotificationSelected: (state, action) => {
      state.notificationSelected = action.payload;
    },
    setOrgNavigationCustomisation: (state, action) => {
      state.orgNavigationCustomisation = action.payload;
    },
    setOrgTranslationKeys: (state, action) => {
      state.orgTranslationKeys = action.payload;
    },
    setOrgGlobalCustomisation: (state, action) => {
      state.orgGlobalCustomization = action.payload;
    },
    setOrgPDPCustomisation: (state, action) => {
      state.orgPDPCustomisation = action.payload;
    },
    setOrgSearchFilterCustomisation: (state, action) => {
      state.orgSearchFilterCustomisation = action.payload;
    },
    setOrgSearchResultCustomisation: (state, action) => {
      state.orgSearchResultCustomisation = action.payload;
    },
    setOrgUnifiedProductPagesCustomisation: (state, action) => {
      state.orgUnifiedProductPagesCustomisation = action.payload;
    },
    setListViewCustomisation: (state, action) => {
      state.listViewCustomisation = action.payload;
    },
    setNavigationCustomisationLoad: (state, action) => {
      state.isNavigationCustomisationLoad = action.payload;
    },
    setGlobalCustomisationLoad: (state, action) => {
      state.isGlobalCustomisationLoad = action.payload;
    },
    setPDPCustomisationLoad: (state, action) => {
      state.isPDPCustomisationLoad = action.payload;
    },
    setSearchFilterCustomisationLoad: (state, action) => {
      state.isSearchFilterCustomisationLoad = action.payload;
    },
    setSearchResultCustomisationLoad: (state, action) => {
      state.isSearchResultCustomisationLoad = action.payload;
    },
    setUnifiedProductPagesLoad: (state, action) => {
      state.isUnifiedProductPagesLoad = action.payload;
    },
    setSearchResultCount: (state, action) => {
      state.searchResultCount = action.payload;
    },
    setInstallationIdentifier: (state, action) => {
      state.installationIdentifier = action.payload;
    },
    setLoadingConfigurations: (state, action) => {
      state.isLoadingConfigurations = action.payload;
    },
    setOverlayInactive: (state, action) => {
      const { screen } = action.payload;
      state.overlayActiveScreens[screen] = false;
    },
    setOverlayState: (state, action) => {
      const { screen, isActive } = action.payload;
      state.overlayActiveScreens[screen] = isActive;
    },
    setOverlayInactiveWeb: (state, action) => {
      const { screen } = action.payload;
      state.overlayActiveScreensWeb[screen] = false;
    },
    setOverlayWebState: (state, action) => {
      const { screen, isActive } = action.payload;
      state.overlayActiveScreensWeb[screen] = isActive;
    },
    clearListViewCustomisation: (state) => {
      state.listViewCustomisation = null;
    },
    setAdHeight: (state, action) => {
      state.adHeight = action.payload;
    },
    setAdWidth: (state, action) => {
      state.adWidth = action.payload;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setLoaderSettings: (state, action) => {
      state.loaderSettings = action.payload;
    },
    setCoachOverlayToast: (state, action) => {
      state.coachOverlayToast = action.payload;
    },
    setLoginModalType: (
      state,
      action: { payload: "default" | "go-back" | "none" }
    ) => {
      state.loginModalType = action.payload;
    },
    setSplitFilterVisible: (state, action) => {
      state.isSplitFilterVisible = action.payload;
    },
    clearSplitFilterVisible: (state) => {
      state.isSplitFilterVisible = null;
    },
    setIsCustomApp: (state, action) => {
      state.isCustomApp = action.payload;
    },
    setAppVersion: (state, action) => {
      state.appVersion = action.payload;
    },
    setFavCollectionVisible: (state, action) => {
      state.isFavCollectionVisible = action.payload;
    },
    clearFavCollectionVisible: (state) => {
      state.isFavCollectionVisible = true;
    },
    updateLastLoginDetails: (state, action) => {
      const { user_id } = action.payload;
      const userLoginDetail = {
        user_id,
        timestamp: new Date().toISOString(),
      };
      let allLoginDetail: any =
        state.lastLoginDetails && state.lastLoginDetails.length
          ? state.lastLoginDetails.filter(
              (detail: any) => detail.user_id !== user_id
            )
          : [];
      allLoginDetail = [...allLoginDetail, userLoginDetail];
      state.lastLoginDetails = allLoginDetail;
    },
    setIsSignoutUrlRedirecting: (state, action) => {
      state.isSignoutUrlRedirecting = action.payload;
    },
    setLatestExportData: (state, action) => {
      state.latestExportData = action.payload;
    },
    clearLatestExportData: (state) => {
      state.latestExportData = null;
    },
    setDisableJewelrySettingStoneLinkage: (state, action) => {
      state.disableJewelrySettingStoneLinkage = action.payload;
    },
  },
});

export const {
  setAppState,
  setOnPreboardingStatus,
  setPostOnboardingCheck,
  resetPostOnboardingCheck,
  setAppInitialLoad,
  setProductType,
  setBuilderProduct,
  setSelectedProduct,
  setNavigationHeight,
  setOrgTheme,
  setIsCompanyTheme,
  setOrgTypography,
  setNotificationSelected,
  setOrgPDPCustomisation,
  setOrgSearchFilterCustomisation,
  setOrgSearchResultCustomisation,
  setOrgNavigationCustomisation,
  setOrgTranslationKeys,
  setOrgUnifiedProductPagesCustomisation,
  setOrgGlobalCustomisation,
  setSearchFilterCustomisationLoad,
  setSearchResultCustomisationLoad,
  setNavigationCustomisationLoad,
  setGlobalCustomisationLoad,
  setPDPCustomisationLoad,
  setUnifiedProductPagesLoad,
  setSearchResultCount,
  setInstallationIdentifier,
  setRequestScreenColor,
  setLoadingConfigurations,
  setOverlayInactive,
  setOverlayState,
  setOverlayInactiveWeb,
  setOverlayWebState,
  setAdHeight,
  setAdWidth,
  setFilterVisible,
  setLoaderSettings,
  setCoachOverlayToast,
  setLoginModalType,
  setListViewCustomisation,
  clearListViewCustomisation,
  setSplitFilterVisible,
  clearSplitFilterVisible,
  setIsCustomApp,
  setAppVersion,
  setFavCollectionVisible,
  clearFavCollectionVisible,
  updateLastLoginDetails,
  setIsSignoutUrlRedirecting,
  setLatestExportData,
  clearLatestExportData,
  setDisableJewelrySettingStoneLinkage,
} = appStateSlice.actions;

// TODO: Add type when we define state
export const useAppState = () => {
  const appState = useSelector((state: any) => state.appState);
  return {
    ...appState,
    onPreBoardingComplete: appState.onPreBoardingComplete,
    postOnboardingCheck: appState.postOnboardingCheck,
    isAppInitialLoad: appState.isAppInitialLoad,
    selectedProduct: appState.selectedProduct,
    activeProduct: appState.activeProduct,
    builderProduct: appState.builderProduct,
    requestScreenColor: appState.requestScreenColor,
    notificationSelected: appState.notificationSelected,
    searchResultCount: appState.searchResultCount,
    isCompanyThemeLoaded: appState.isCompanyThemeLoaded,
    isNavigationCustomisationLoad: appState.isNavigationCustomisationLoad,
    isPDPCustomisationLoad: appState.isPDPCustomisationLoad,
    installationIdentifier: appState.installationIdentifier,
    isSearchFilterCustomisationLoad: appState.isSearchFilterCustomisationLoad,
    isSearchResultCustomisationLoad: appState.isSearchResultCustomisationLoad,
    isUnifiedProductPagesLoad: appState.isUnifiedProductPagesLoad,
    overlayActiveScreens: appState.overlayActiveScreens,
    overlayActiveScreensWeb: appState.overlayActiveScreensWeb,
    loaderSettings: appState.loaderSettings,
    coachOverlayToast: appState.coachOverlayToast,
    isSplitFilterVisible: appState.isSplitFilterVisible,
    isCustomApp: appState.isCustomApp,
    appVersion: appState.appVersion,
    lastLoginDetails: appState.lastLoginDetails,
    isSignoutUrlRedirecting: appState.isSignoutUrlRedirecting,
    latestExportData: appState.latestExportData,
    disableJewelrySettingStoneLinkage:
      appState.disableJewelrySettingStoneLinkage,
  };
};

export default appStateSlice.reducer;
